import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

const GIFT_PROPERTY = '_wondear_bogo_gift';
const ELIGIBLE_PROPERTY = '_wondear_bogo_for';
const OFFER_PROPERTY = '_wondear_bogo_offer';
let cartSyncInProgress = false;
let cartSyncTimer;

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

function cartRequestBody(extra = {}) {
  const sections = getCartSectionIds();
  return {
    ...extra,
    ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
  };
}

async function fetchCart() {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to read cart');
  return response.json();
}

function scheduleGiftSync(event) {
  if (event?.detail?.data?.source === 'bogo-free-gift-sync') return;
  window.clearTimeout(cartSyncTimer);
  cartSyncTimer = window.setTimeout(syncGiftQuantities, 120);
}

async function syncGiftQuantities() {
  if (cartSyncInProgress) return;
  cartSyncInProgress = true;

  try {
    const cart = await fetchCart();
    const giftLines = cart.items.filter((item) => item.properties?.[GIFT_PROPERTY] === 'true');
    if (!giftLines.length) return;

    const groups = new Map();
    giftLines.forEach((line) => {
      const eligibleProductId = String(line.properties?.[ELIGIBLE_PROPERTY] || '');
      if (!eligibleProductId) return;
      if (!groups.has(eligibleProductId)) groups.set(eligibleProductId, []);
      groups.get(eligibleProductId).push(line);
    });

    let lastResponse;

    for (const [eligibleProductId, lines] of groups) {
      const eligibleQuantity = cart.items
        .filter(
          (item) =>
            String(item.product_id) === eligibleProductId && item.properties?.[GIFT_PROPERTY] !== 'true'
        )
        .reduce((total, item) => total + item.quantity, 0);

      for (let index = 0; index < lines.length; index += 1) {
        const desiredQuantity = index === 0 ? eligibleQuantity : 0;
        if (lines[index].quantity === desiredQuantity) continue;

        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(cartRequestBody({ id: lines[index].key, quantity: desiredQuantity })),
        });

        lastResponse = await response.json();
        if (!response.ok || lastResponse.errors) throw new Error(lastResponse.description || 'Unable to update gift');
      }
    }

    if (lastResponse) {
      document.dispatchEvent(
        new CartUpdateEvent(lastResponse, 'bogo-free-gift-sync', {
          source: 'bogo-free-gift-sync',
          itemCount: lastResponse.item_count,
          sections: lastResponse.sections,
        })
      );
    }
  } catch (error) {
    console.error('[Wondear BOGO]', error);
  } finally {
    cartSyncInProgress = false;
  }
}

class BogoFreeGift extends HTMLElement {
  #addingGift = false;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleEligibleProductAdded);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleEligibleProductAdded);
  }

  #handleEligibleProductAdded = async (event) => {
    if (this.dataset.autoAdd !== 'true' || this.#addingGift) return;
    if (event.detail?.data?.didError) return;
    if (String(event.detail?.data?.productId || '') !== this.dataset.eligibleProductId) return;
    if (event.detail?.data?.source === 'bogo-free-gift') return;

    const quantity = Math.max(1, Number.parseInt(event.detail?.data?.itemCount || '1', 10));
    const giftVariantId = Number.parseInt(this.dataset.giftVariantId || '', 10);
    if (!Number.isFinite(giftVariantId)) return;

    this.#addingGift = true;
    this.#setStatus(this.dataset.addingText || 'Adding your free gift…', 'loading');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          cartRequestBody({
            items: [
              {
                id: giftVariantId,
                quantity,
                properties: {
                  [GIFT_PROPERTY]: 'true',
                  [ELIGIBLE_PROPERTY]: this.dataset.eligibleProductId,
                  [OFFER_PROPERTY]: this.dataset.offerName || 'Buy 1 Get 1 Free',
                },
              },
            ],
          })
        ),
      });

      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Unable to add gift');

      this.#setStatus(this.dataset.addedText || 'Free 3D Mask added to cart!', 'success');
      document.dispatchEvent(
        new CartAddEvent(result, 'bogo-free-gift', {
          source: 'bogo-free-gift',
          productId: this.dataset.giftProductId,
          itemCount: quantity,
          sections: result.sections,
        })
      );
    } catch (error) {
      this.#setStatus(this.dataset.errorText || 'Gift could not be added. Please try again.', 'error');
      console.error('[Wondear BOGO]', error);
    } finally {
      this.#addingGift = false;
    }
  };

  #setStatus(message, state) {
    const status = this.querySelector('[data-gift-status]');
    if (!status) return;
    status.textContent = message;
    status.dataset.state = state;
  }
}

if (!customElements.get('bogo-free-gift')) {
  customElements.define('bogo-free-gift', BogoFreeGift);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleGiftSync);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.setTimeout(syncGiftQuantities, 250), { once: true });
} else {
  window.setTimeout(syncGiftQuantities, 250);
}
